import { useEffect, useState, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { airports } from "@/data/airports";

interface NearbyAirportResult {
  code: string;
  name: string;
  distanceMiles: number;
}

interface LocationState {
  isSupported: boolean;
  permissionStatus: "prompt" | "granted" | "denied" | "unknown";
  nearbyAirport: NearbyAirportResult | null;
  isLocating: boolean;
}

// Calculate distance between two coordinates in miles using Haversine formula
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Airport coordinates for major US airports
const airportCoordinates: Record<string, { lat: number; lon: number }> = {
  ATL: { lat: 33.6407, lon: -84.4277 },
  LAX: { lat: 33.9425, lon: -118.4081 },
  ORD: { lat: 41.9742, lon: -87.9073 },
  DFW: { lat: 32.8998, lon: -97.0403 },
  DEN: { lat: 39.8561, lon: -104.6737 },
  JFK: { lat: 40.6413, lon: -73.7781 },
  SFO: { lat: 37.6213, lon: -122.379 },
  SEA: { lat: 47.4502, lon: -122.3088 },
  MIA: { lat: 25.7959, lon: -80.287 },
  EWR: { lat: 40.6895, lon: -74.1745 },
  MCO: { lat: 28.4312, lon: -81.308 },
  BOS: { lat: 42.3656, lon: -71.0096 },
  MSP: { lat: 44.8848, lon: -93.2223 },
  DTW: { lat: 42.2162, lon: -83.3554 },
  PHL: { lat: 39.8744, lon: -75.2424 },
  LGA: { lat: 40.7769, lon: -73.8740 },
  IAH: { lat: 29.9902, lon: -95.3368 },
  PHX: { lat: 33.4373, lon: -112.0078 },
  CLT: { lat: 35.214, lon: -80.9431 },
  SAN: { lat: 32.7338, lon: -117.1933 },
  TPA: { lat: 27.9756, lon: -82.5333 },
  IAD: { lat: 38.9531, lon: -77.4565 },
  DCA: { lat: 38.8512, lon: -77.0402 },
  AUS: { lat: 30.1975, lon: -97.6664 },
  BNA: { lat: 36.1263, lon: -86.6774 },
  PDX: { lat: 45.5898, lon: -122.5951 },
  STL: { lat: 38.7487, lon: -90.37 },
  HNL: { lat: 21.3187, lon: -157.9225 },
  SLC: { lat: 40.7899, lon: -111.9791 },
  MDW: { lat: 41.786, lon: -87.7524 },
  RDU: { lat: 35.8801, lon: -78.7880 },
  SJC: { lat: 37.3626, lon: -121.929 },
  OAK: { lat: 37.7213, lon: -122.2208 },
  SMF: { lat: 38.6954, lon: -121.5908 },
  MCI: { lat: 39.2976, lon: -94.7139 },
  CLE: { lat: 41.4117, lon: -81.8498 },
  IND: { lat: 39.7173, lon: -86.2944 },
  CMH: { lat: 39.998, lon: -82.8919 },
  PIT: { lat: 40.4915, lon: -80.2329 },
  SAT: { lat: 29.5337, lon: -98.4698 },
  MKE: { lat: 42.9472, lon: -87.8966 },
  MSY: { lat: 29.9934, lon: -90.258 },
  RSW: { lat: 26.5362, lon: -81.7552 },
  JAX: { lat: 30.4941, lon: -81.6879 },
  BDL: { lat: 41.9389, lon: -72.6832 },
  ABQ: { lat: 35.0402, lon: -106.6094 },
  OMA: { lat: 41.3032, lon: -95.8941 },
};

const NEARBY_THRESHOLD_MILES = 30;

export const useNearbyAirport = () => {
  const [state, setState] = useState<LocationState>({
    isSupported: false,
    permissionStatus: "unknown",
    nearbyAirport: null,
    isLocating: false,
  });

  const findNearbyAirport = useCallback(
    (latitude: number, longitude: number): NearbyAirportResult | null => {
      let closest: NearbyAirportResult | null = null;
      let minDistance = Infinity;

      for (const [code, coords] of Object.entries(airportCoordinates)) {
        const distance = haversineDistance(latitude, longitude, coords.lat, coords.lon);
        if (distance < minDistance && distance <= NEARBY_THRESHOLD_MILES) {
          minDistance = distance;
          const airportData = airports.find(
            (a) => a.code.toUpperCase() === code.toUpperCase()
          );
          closest = {
            code,
            name: airportData?.name || code,
            distanceMiles: Math.round(distance * 10) / 10,
          };
        }
      }

      return closest;
    },
    []
  );

  const requestLocation = useCallback(async () => {
    setState((prev) => ({ ...prev, isLocating: true }));

    try {
      if (Capacitor.isNativePlatform()) {
        const { Geolocation } = await import("@capacitor/geolocation");
        const permResult = await Geolocation.requestPermissions();

        setState((prev) => ({
          ...prev,
          permissionStatus: permResult.location as LocationState["permissionStatus"],
        }));

        if (permResult.location === "granted") {
          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false,
            timeout: 10000,
          });

          const nearby = findNearbyAirport(
            position.coords.latitude,
            position.coords.longitude
          );
          setState((prev) => ({ ...prev, nearbyAirport: nearby, isLocating: false }));
          return nearby;
        }
      } else if ("geolocation" in navigator) {
        // Web fallback
        return new Promise<NearbyAirportResult | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const nearby = findNearbyAirport(
                position.coords.latitude,
                position.coords.longitude
              );
              setState((prev) => ({
                ...prev,
                nearbyAirport: nearby,
                isLocating: false,
                permissionStatus: "granted",
              }));
              resolve(nearby);
            },
            () => {
              setState((prev) => ({
                ...prev,
                isLocating: false,
                permissionStatus: "denied",
              }));
              resolve(null);
            },
            { enableHighAccuracy: false, timeout: 10000 }
          );
        });
      }
    } catch (err) {
      console.error("Location error:", err);
    }

    setState((prev) => ({ ...prev, isLocating: false }));
    return null;
  }, [findNearbyAirport]);

  useEffect(() => {
    const isSupported =
      Capacitor.isNativePlatform() || "geolocation" in navigator;
    setState((prev) => ({ ...prev, isSupported }));
  }, []);

  return {
    ...state,
    requestLocation,
  };
};
